/**
 * Defensive URL filter + safe-anchor attribute helper for assistant-emitted
 * links rendered inside the widget chat panel.
 *
 * Issue: https://github.com/Scale2Rev/scale2rev-platform/issues/215
 * Parity: TSAI `sanitizeAssistantHtml` in `public/widget/uiController.js`.
 *
 * Why this exists
 * ---------------
 * Assistant messages can contain links — either explicit Markdown `[text](url)`
 * syntax or bare URLs that `remark-gfm`'s autolink-literal plugin promotes to
 * `<a>` elements. Either path lets the model influence the rendered DOM's
 * `href`, which is an XSS vector if we don't filter unsafe schemes.
 *
 * `react-markdown` v10 removed `transformLinkUri` (its v9 default URL filter)
 * in favor of caller-side filtering in the custom `a` component. This helper
 * is that filter, kept pure so the policy is unit-testable in isolation from
 * the React render path.
 *
 * Policy (matches TSAI: http / https / mailto / tel only)
 * -------------------------------------------------------
 *   - **Allowed schemes**: `http:`, `https:`, `mailto:`, `tel:` — visible in
 *     TSAI's anchor normalization pass and reasonable for a chat surface.
 *   - **Allowed shape**: bare relative path / fragment / anonymous protocol
 *     (`//host/...`) — explicitly REJECTED because the widget is embedded on
 *     third-party origins; a relative link rendered there would resolve
 *     against the customer's site, not the tenant's. Better to drop the link.
 *   - **Rejected**: `javascript:`, `data:`, `vbscript:`, `file:`, `blob:`,
 *     unknown schemes, malformed strings, and bare relative paths. All of
 *     these collapse to "render the link text without an `href`".
 *
 * Attribute pass
 * --------------
 * For allowed http(s) links we additionally enforce `target="_blank"` +
 * `rel="noopener noreferrer"` — same as TSAI's `target="_blank"` + safe-rel
 * normalization. `mailto:` / `tel:` links get neither (they don't navigate).
 */

const ALLOWED_NAVIGATION_SCHEMES = new Set(['http:', 'https:']);
const ALLOWED_NON_NAVIGATION_SCHEMES = new Set(['mailto:', 'tel:']);

export interface SafeAnchorAttrs {
  /** Final href value to render. `null` means render link text without an anchor. */
  readonly href: string | null;
  /** `_blank` only for http(s); `undefined` for mailto/tel/null. */
  readonly target: '_blank' | undefined;
  /** `noopener noreferrer` only for http(s); `undefined` for mailto/tel/null. */
  readonly rel: 'noopener noreferrer' | undefined;
}

/**
 * Returns `true` iff `href` is non-empty, parses as an absolute URL, and uses
 * one of the four allowed schemes. Pure; no DOM, no network.
 */
export function isSafeHref(href: string | null | undefined): boolean {
  if (typeof href !== 'string') return false;
  const trimmed = href.trim();
  if (trimmed.length === 0) return false;

  // Protocol-relative URLs (`//host/...`) are rejected — they would resolve
  // against the customer's embed origin, not the tenant's intent.
  if (trimmed.startsWith('//')) return false;

  // Fragments / pure relative paths are rejected for the same reason: the
  // widget is embedded on third-party pages so a `/contact` link would land
  // on the customer's site, not the tenant's.
  if (trimmed.startsWith('/') || trimmed.startsWith('#') || trimmed.startsWith('?')) return false;

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return false;
  }

  const scheme = parsed.protocol.toLowerCase();
  return ALLOWED_NAVIGATION_SCHEMES.has(scheme) || ALLOWED_NON_NAVIGATION_SCHEMES.has(scheme);
}

/**
 * Derive the final anchor attribute set for an assistant-emitted link.
 *
 * Returns `{ href: null }` for any rejected URL so the React component can
 * collapse the link to plain text without an anchor element.
 */
export function getSafeAnchorAttrs(href: string | null | undefined): SafeAnchorAttrs {
  if (!isSafeHref(href)) {
    return { href: null, target: undefined, rel: undefined };
  }

  const trimmed = (href as string).trim();
  const parsed = new URL(trimmed);
  const scheme = parsed.protocol.toLowerCase();

  if (ALLOWED_NAVIGATION_SCHEMES.has(scheme)) {
    return { href: trimmed, target: '_blank', rel: 'noopener noreferrer' };
  }

  // mailto: / tel: — no new tab, no rel.
  return { href: trimmed, target: undefined, rel: undefined };
}
